<template>
  <div class="google-page">
    <el-card shadow="never" class="header-card">
      <div class="page-header">
        <div>
          <h2>任务结果</h2>
          <p>查看已提交广告的创建结果和投放信息。</p>
        </div>
        <div class="header-actions">
          <el-button
            size="small"
            icon="el-icon-download"
            :loading="downloadLoading"
            @click="handleDownloadTemplate"
          >
            下载模板
          </el-button>
          <el-button
            size="small"
            icon="el-icon-refresh"
            :loading="refreshLoading"
            @click="refreshStatus"
          >
            刷新状态
          </el-button>
          <el-button
            size="small"
            icon="el-icon-document"
            :loading="exportLoading"
            @click="handleExport"
          >
            导出 Excel
          </el-button>
          <el-button
            type="primary"
            size="small"
            icon="el-icon-plus"
            :loading="importLoading"
            @click="triggerImport"
          >
            创建广告
          </el-button>
          <input
            ref="excelInput"
            type="file"
            accept=".xlsx,.xls,.csv"
            class="hidden-input"
            @change="handleExcelChange"
          />
        </div>
      </div>
    </el-card>

    <el-card shadow="never" class="table-card">
      <div class="table-toolbar">
        <div class="toolbar-left">
          <span class="table-title">广告列表</span>
          <el-select
            v-model="filterCustomerId"
            size="small"
            clearable
            placeholder="全部 Customer"
            style="width: 280px"
            @change="handleFilter"
          >
            <el-option label="全部 Customer" value="" />
            <el-option
              v-for="item in customers"
              :key="item.customerId"
              :label="customerOptionLabel(item)"
              :value="item.customerId"
            />
          </el-select>
          <span class="total-text">共 {{ total }} 条</span>
        </div>
      </div>

      <el-table
        v-loading="tableLoading"
        :data="tableData"
        border
        stripe
        size="small"
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="90" align="center" />
        <el-table-column label="Customer ID" width="150" align="center">
          <template slot-scope="scope">
            {{ formatCustomerId(scope.row.customerId) }}
          </template>
        </el-table-column>
        <el-table-column label="站点" min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ getCustomerSite(scope.row.customerId) }}
          </template>
        </el-table-column>
        <el-table-column prop="adsName" label="广告系列名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="campaignConversionGoalCategory" label="转化" width="120" align="center" />
        <el-table-column label="预算" width="120" align="center">
          <template slot-scope="scope">
            {{ formatBudgetMicros(scope.row.campaignBudgetAmountMicros) }}
          </template>
        </el-table-column>
        <el-table-column prop="selectedChannel" label="频道" width="140" align="center" />
        <el-table-column label="落地页链接" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <a
              v-if="scope.row.finalUrl"
              :href="scope.row.finalUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="link-text"
            >
              {{ scope.row.finalUrl }}
            </a>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="uploadTime" label="导入时间" width="170" align="center" />
        <el-table-column label="状态" width="110" align="center">
          <template slot-scope="scope">
            <span class="status-pill" :class="'is-' + mapUploadStatus(scope.row.uploadStatus).type">
              {{ mapUploadStatus(scope.row.uploadStatus).label }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="statusDesc" label="状态返回" min-width="220" show-overflow-tooltip />
      </el-table>

      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
  import {
    UPLOAD_STATUS,
    customerOptionLabel,
    downloadAdTemplate,
    exportAds,
    formatBudgetMicros,
    formatCustomerId,
    importAds,
    listAds,
    listCustomers,
    mapUploadStatus
  } from '@/api/googleAds'
  import { downloadExcel } from '@/utils/excel'

  export default {
    name: 'GoogleCreateAd',
    data() {
      return {
        customers: [],
        filterCustomerId: '',
        tableData: [],
        tableLoading: false,
        currentPage: 1,
        pageSize: 20,
        total: 0,
        downloadLoading: false,
        refreshLoading: false,
        exportLoading: false,
        importLoading: false,
        pollTimer: null
      }
    },
    mounted() {
      this.initPage()
    },
    beforeDestroy() {
      this.clearPoll()
    },
    methods: {
      formatBudgetMicros,
      formatCustomerId,
      customerOptionLabel,
      mapUploadStatus,
      getCustomerSite(customerId) {
        const item = this.customers.find((customer) => customer.customerId === customerId)
        return (item && item.webSiteUrl) || '-'
      },
      async initPage() {
        await this.loadCustomers()
        await this.loadData()
      },
      async loadCustomers() {
        try {
          const response = await listCustomers()
          this.customers = response.body.data || []
        } catch (error) {
          this.$message.error(error.message || '加载 Customer 失败')
        }
      },
      async loadData(silent = false) {
        if (!silent) this.tableLoading = true
        try {
          const response = await listAds({
            customerId: this.filterCustomerId,
            pageNum: this.currentPage,
            pageSize: this.pageSize
          })
          const list = Array.isArray(response.body && response.body.data)
            ? response.body.data
            : []
          this.total = (response.body && response.body.total) || 0
          if (silent) {
            this.tableData.forEach((row, index) => {
              const fresh = list.find((item) => item.id === row.id)
              if (fresh) this.$set(this.tableData, index, fresh)
            })
          } else {
            this.tableData = list
          }
          this.setupPoll()
        } catch (error) {
          this.$message.error(error.message || '加载广告失败')
        } finally {
          if (!silent) this.tableLoading = false
        }
      },
      handleFilter() {
        this.currentPage = 1
        this.loadData()
      },
      async refreshStatus() {
        this.refreshLoading = true
        try {
          await this.loadData()
          this.$message.success('状态已刷新')
        } finally {
          this.refreshLoading = false
        }
      },
      async handleDownloadTemplate() {
        this.downloadLoading = true
        try {
          await downloadAdTemplate()
          this.$message.success('模板已开始下载')
        } catch (error) {
          this.$message.error(error.message || '模板下载失败')
        } finally {
          this.downloadLoading = false
        }
      },
      async handleExport() {
        this.exportLoading = true
        try {
          const result = await exportAds({
            customerId: this.filterCustomerId
          })
          if (result.mode === 'empty') {
            await this.exportCurrentList()
          }
          this.$message.success('导出成功')
        } catch (error) {
          try {
            await this.exportCurrentList()
            this.$message.success('导出成功')
          } catch (fallbackError) {
            this.$message.error(error.message || '导出失败')
          }
        } finally {
          this.exportLoading = false
        }
      },
      async exportCurrentList() {
        const response = await listAds({
          customerId: this.filterCustomerId,
          pageNum: 1,
          pageSize: 10000
        })
        const rows = (Array.isArray(response.body && response.body.data) ? response.body.data : []).map((item) => [
          item.id,
          item.customerId,
          this.getCustomerSite(item.customerId),
          item.adsName,
          item.campaignConversionGoalCategory,
          this.formatBudgetMicros(item.campaignBudgetAmountMicros),
          item.selectedChannel,
          item.finalUrl,
          item.uploadTime,
          this.mapUploadStatus(item.uploadStatus).label,
          item.statusDesc
        ])
        downloadExcel('Google广告列表.xlsx', [
          {
            name: '广告列表',
            header: ['ID', 'Customer ID', '站点', '广告系列名称', '转化', '预算', '频道', '落地页链接', '导入时间', '状态', '状态返回'],
            rows
          }
        ])
      },
      triggerImport() {
        this.$refs.excelInput.value = ''
        this.$refs.excelInput.click()
      },
      async handleExcelChange(event) {
        const file = event.target.files && event.target.files[0]
        if (!file) return

        const isExcel =
          /\.(xlsx|xls|csv)$/i.test(file.name) ||
          [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
            'text/csv'
          ].includes(file.type)
        if (!isExcel) {
          this.$message.error('请上传 Excel 文件')
          return
        }
        if (file.size / 1024 / 1024 > 10) {
          this.$message.error('文件大小不能超过 10MB')
          return
        }

        this.importLoading = true
        try {
          await this.$confirm('确认导入该 Excel 并创建广告吗？', '创建广告', {
            confirmButtonText: '确定导入',
            cancelButtonText: '取消',
            type: 'warning'
          })

          await importAds(file)
          this.$message.success('已提交创建任务')
          this.currentPage = 1
          await this.loadData()
        } catch (error) {
          if (error !== 'cancel') {
            this.$message.error(error.message || '导入失败')
          }
        } finally {
          this.importLoading = false
          event.target.value = ''
        }
      },
      handleSizeChange(val) {
        this.pageSize = val
        this.currentPage = 1
        this.loadData()
      },
      handleCurrentChange(val) {
        this.currentPage = val
        this.loadData()
      },
      setupPoll() {
        this.clearPoll()
        const hasPending = this.tableData.some(
          (item) => Number(item.uploadStatus) === UPLOAD_STATUS.UPLOADING
        )
        if (!hasPending) return
        this.pollTimer = setInterval(() => {
          this.loadData(true)
        }, 4000)
      },
      clearPoll() {
        if (this.pollTimer) {
          clearInterval(this.pollTimer)
          this.pollTimer = null
        }
      }
    }
  }
</script>

<style scoped>
  .google-page {
    min-height: calc(100vh - 60px);
    padding: 20px;
    background: #f5f7fa;
  }

  .header-card,
  .table-card {
    border-radius: 8px;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }

  .page-header h2 {
    margin: 0 0 6px;
    font-size: 20px;
    font-weight: 600;
    color: #303133;
  }

  .page-header p {
    margin: 0;
    color: #909399;
    font-size: 13px;
    line-height: 1.6;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    flex-wrap: wrap;
  }

  .hidden-input {
    display: none;
  }

  .table-card {
    margin-top: 16px;
  }

  .table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .toolbar-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .table-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  .total-text {
    color: #909399;
    font-size: 13px;
  }

  .link-text {
    color: #409eff;
    text-decoration: none;
  }

  .link-text:hover {
    text-decoration: underline;
  }

  .pagination-container {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
  }

  .status-pill {
    display: inline-block;
    min-width: 64px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    border: 1px solid;
    line-height: 1.6;
  }

  .status-pill.is-success {
    color: #67c23a;
    background: #f0f9eb;
    border-color: #c2e7b0;
  }

  .status-pill.is-warning {
    color: #e6a23c;
    background: #fdf6ec;
    border-color: #f5dab1;
  }

  .status-pill.is-danger {
    color: #f56c6c;
    background: #fef0f0;
    border-color: #fbc4c4;
  }

  .status-pill.is-info {
    color: #909399;
    background: #f4f4f5;
    border-color: #d3d4d6;
  }

  @media (max-width: 768px) {
    .google-page {
      padding: 12px;
    }

    .page-header {
      flex-direction: column;
    }

    .pagination-container {
      justify-content: center;
    }
  }
</style>
